// B5-DECIDE: the Setup › Decisions queries. The keys sit under `["company", co, …]`, so the
// company's change pings (`company.updated`, `decision_rubric.saved`) refresh them.
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type DecideSettings = OperationOutput<"decide.settings.get">;
export type DecideSettingsUpdate = OperationInput<"decide.settings.update">["body"];
export type Rubric = OperationOutput<"decisionRubrics.list">["data"][number];
export type RubricDetail = OperationOutput<"decisionRubrics.get">;
export type RubricEdit = OperationInput<"decisionRubrics.edit">["body"];
export type RubricQuestion = Rubric["questions"][number];

export const decideKeys = {
  settings: (co: string) => ["company", co, "decide", "settings"] as const,
  rubrics: (co: string) => ["company", co, "decide", "rubrics"] as const,
  rubric: (co: string, id: string) => ["company", co, "decide", "rubrics", id] as const,
};

export const decideSettingsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: decideKeys.settings(co),
    queryFn: () => api.client.decide.settings.get({ params: { co } }),
  });

export const rubricsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: decideKeys.rubrics(co),
    queryFn: () => api.client.decisionRubrics.list({ params: { co } }),
  });

export const rubricQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: decideKeys.rubric(co, id),
    queryFn: () => api.client.decisionRubrics.get({ params: { co, id } }),
  });

/** The form's state: the editable text of a rubric (keys and question structure stay). */
export type RubricDraft = {
  title: string;
  focus: string;
  act: string;
  review: string;
  note: string;
  questions: {
    question: string;
    focus: string;
    options: { what: string; not_for: string; examples: string }[];
  }[];
};

export function draftOfRubric(r: Rubric): RubricDraft {
  return {
    title: r.title,
    focus: r.focus ?? "",
    act: String(r.act_threshold),
    review: String(r.review_threshold),
    note: "",
    questions: r.questions.map((q) => ({
      question: q.question,
      focus: q.focus ?? "",
      options: q.options.map((o) => ({
        what: o.what,
        not_for: o.not_for ?? "",
        examples: (o.examples ?? []).join("\n"),
      })),
    })),
  };
}

const opt = (s: string) => (s.trim() ? s.trim() : undefined);

/** The edit body, or the first problem a person must fix. */
export function editOf(r: Rubric, d: RubricDraft): { body: RubricEdit } | { problem: string } {
  const title = d.title.trim();
  if (!title) return { problem: "The rubric needs a title." };
  if (/[\r\n]/.test(title)) return { problem: "The title is one line." };
  const act = Number(d.act);
  const review = Number(d.review);
  if (!d.act.trim() || !(act > 0 && act <= 1))
    return { problem: "Act at must be above 0 and at most 1." };
  if (!d.review.trim() || !(review > 0 && review < 1))
    return { problem: "Review at must be between 0 and 1." };
  if (review >= act) return { problem: "Review at must be below Act at." };
  const questions: RubricQuestion[] = [];
  for (const [qi, q] of r.questions.entries()) {
    const dq = d.questions[qi];
    if (!dq) return { problem: "The questions changed; reopen the editor." };
    if (!dq.question.trim()) return { problem: `Question ${q.key} needs its text.` };
    const options = [];
    for (const [oi, o] of q.options.entries()) {
      const dopt = dq.options[oi];
      if (!dopt?.what.trim()) return { problem: `${q.key} › ${o.key} needs a "what".` };
      const examples = dopt.examples
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      if (examples.length > 8) return { problem: `${q.key} › ${o.key}: at most 8 examples.` };
      const notFor = opt(dopt.not_for);
      options.push({
        key: o.key,
        what: dopt.what.trim(),
        ...(notFor ? { not_for: notFor } : {}),
        ...(examples.length ? { examples } : {}),
      });
    }
    const qFocus = opt(dq.focus);
    questions.push({
      key: q.key,
      type: q.type,
      question: dq.question.trim(),
      ...(qFocus ? { focus: qFocus } : {}),
      options,
    });
  }
  const note = d.note.trim();
  if (/[\r\n]/.test(note)) return { problem: "The note is one line." };
  return {
    body: {
      title,
      focus: opt(d.focus) ?? null,
      questions,
      act_threshold: act,
      review_threshold: review,
      ...(note ? { note } : {}),
    },
  };
}

/** `0.7` → `70%`. */
export const pct = (n: number) => `${Math.round(n * 100)}%`;
